import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { SearchResult } from '../../types/market';
import { TradierService } from '../../services/TradierService';
import { LoadingSpinner } from './LoadingSpinner';
import { Search } from 'lucide-react';

interface HeaderSearchBarProps {
  className?: string;
  disabled?: boolean;
}

export const HeaderSearchBar: React.FC<HeaderSearchBarProps> = ({
  className = '',
  disabled = false,
}) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [error, setError] = useState<string | null>(null);
  const [focused, setFocused] = useState(false);

  const navigate = useNavigate();
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const tradierService = useRef(new TradierService());

  const debouncedSearch = useCallback(
    debounce(async (q: string) => {
      if (!q.trim()) { setResults([]); setIsOpen(false); return; }
      setIsLoading(true);
      setError(null);
      try {
        const res = await tradierService.current.searchStocks(q);
        setResults(res);
        setIsOpen(res.length > 0);
        setSelectedIndex(-1);
      } catch {
        setError('Search failed');
        setResults([]);
        setIsOpen(false);
      } finally {
        setIsLoading(false);
      }
    }, 300),
    []
  );

  useEffect(() => { debouncedSearch(query); }, [query, debouncedSearch]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setSelectedIndex(-1);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen || !results.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelectedIndex(p => (p < results.length - 1 ? p + 1 : 0)); }
    if (e.key === 'ArrowUp')   { e.preventDefault(); setSelectedIndex(p => (p > 0 ? p - 1 : results.length - 1)); }
    if (e.key === 'Enter')     { e.preventDefault(); if (selectedIndex >= 0) handleSelect(results[selectedIndex]); }
    if (e.key === 'Escape')    { setIsOpen(false); setSelectedIndex(-1); inputRef.current?.blur(); }
  };

  const handleSelect = (result: SearchResult) => {
    setQuery('');
    setIsOpen(false);
    setSelectedIndex(-1);
    navigate(`/stock/${result.symbol}`);
  };

  return (
    <div ref={searchRef} className={className} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <div style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
          <Search size={14} color={focused ? '#3b82f6' : '#334155'} strokeWidth={2} style={{ transition: 'color 0.18s' }} />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={e => setQuery(e.target.value)}
          onKeyDown={handleKeyDown}
          onFocus={() => { setFocused(true); if (results.length) setIsOpen(true); }}
          onBlur={() => setFocused(false)}
          placeholder="Search stocks…"
          disabled={disabled}
          autoComplete="off"
          aria-label="Search stocks"
          style={{
            width: '100%',
            padding: '0.5rem 2rem 0.5rem 2rem',
            background: '#1a2235',
            border: `1px solid ${focused ? '#3b82f6' : '#1e2d45'}`,
            borderRadius: 8,
            color: '#f1f5f9',
            fontSize: '0.8125rem',
            fontFamily: 'inherit',
            outline: 'none',
            transition: 'border-color 0.18s, box-shadow 0.18s',
            boxShadow: focused ? '0 0 0 2px rgba(59,130,246,0.15)' : 'none',
            opacity: disabled ? 0.5 : 1,
          }}
        />
        {isLoading && (
          <div style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}>
            <LoadingSpinner size="sm" />
          </div>
        )}
      </div>

      {isOpen && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 6px)', left: 0, right: 0,
          background: '#111827',
          border: '1px solid #1e2d45',
          borderRadius: 10,
          boxShadow: '0 16px 48px rgba(0,0,0,0.55)',
          maxHeight: 260, overflowY: 'auto',
          zIndex: 100,
          animation: 'slideUp 0.18s ease-out',
        }}>
          {error ? (
            <div style={{ padding: '0.625rem 0.875rem', fontSize: '0.8rem', color: '#f87171' }}>{error}</div>
          ) : results.map((result, i) => (
            <div
              key={`${result.symbol}-${result.exchange}`}
              onClick={() => handleSelect(result)}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '0.5rem 0.875rem',
                cursor: 'pointer',
                background: i === selectedIndex ? '#1a2235' : 'transparent',
                borderBottom: '1px solid #0f1929',
                transition: 'background 0.12s',
              }}
              onMouseEnter={e => { e.currentTarget.style.background = '#1a2235'; }}
              onMouseLeave={e => { e.currentTarget.style.background = i === selectedIndex ? '#1a2235' : 'transparent'; }}
            >
              <div>
                <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: '#f1f5f9', fontFamily: "'JetBrains Mono', monospace", letterSpacing: '0.02em' }}>
                  {result.symbol}
                </div>
                <div style={{ fontSize: '0.73rem', color: '#475569', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {result.name}
                </div>
              </div>
              {result.exchange && (
                <span style={{ fontSize: '0.68rem', fontWeight: 600, color: '#334155', background: '#0f1929', padding: '0.15rem 0.4rem', borderRadius: 5, flexShrink: 0 }}>
                  {result.exchange}
                </span>
              )}
            </div>
          ))}
          {!error && results.length === 0 && query.trim() && !isLoading && (
            <div style={{ padding: '0.75rem', fontSize: '0.8rem', color: '#334155', textAlign: 'center' }}>
              No results for "{query}"
            </div>
          )}
        </div>
      )}
    </div>
  );
};

function debounce<T extends (...args: any[]) => any>(fn: T, wait: number) {
  let t: ReturnType<typeof setTimeout>;
  return (...args: Parameters<T>) => { clearTimeout(t); t = setTimeout(() => fn(...args), wait); };
}
